// English source strings for modules 14 (safety, occupancy, muster) and 15 (reporting, analytics,
// audit reporting) — safety & reporting stream, ADR-019. Arabic: ar-safety.ts.
export const enSafety = {
  // ------------------------------------------------------------ reporting: definitions
  'rpt.def.operations.dashboard.title': 'Operational dashboard',
  'rpt.def.operations.dashboard.description': 'Expected, arrived, waiting, onsite, overdue, denied, cancelled and checked-out activity for the selected sites and period.',
  'rpt.def.visits.summary.title': 'Visit report',
  'rpt.def.visits.summary.description': 'Visit volumes, outcomes and durations by site, day, visitor type, organisation, destination, purpose or source.',
  'rpt.def.visits.exceptions.title': 'No-shows and unresolved departures',
  'rpt.def.visits.exceptions.description': 'Scheduled visitors who never arrived and people whose departure is still unresolved, with the history of each classification.',
  'rpt.def.visits.hosts.title': 'Host and department activity',
  'rpt.def.visits.hosts.description': 'Visit activity by host or department, with the department attribution shown.',
  'rpt.def.contractors.compliance.title': 'Contractor attendance and compliance',
  'rpt.def.contractors.compliance.description': 'Contractor attendance with compliance as it stood at each arrival, beside today’s company standing, permits and expiring evidence.',
  'rpt.def.vehicles.summary.title': 'Vehicles and parking',
  'rpt.def.vehicles.summary.description': 'Vehicle entries and exits, parking use, camera reads, unmatched plates and gate exceptions — counted separately from people.',

  // ------------------------------------------------------------ reporting: metrics and definitions
  'rpt.metric.expected': 'Expected',
  'rpt.metric.arrived': 'Arrived',
  'rpt.metric.checked_out': 'Checked out',
  'rpt.metric.denied': 'Denied',
  'rpt.metric.cancelled': 'Cancelled',
  'rpt.metric.waiting': 'Waiting',
  'rpt.metric.onsite': 'Onsite',
  'rpt.metric.uncertain': 'Presence uncertain',
  'rpt.metric.overdue': 'Overdue',
  'rpt.metric.people': 'People in period',
  'rpt.def.expected': 'Participants cleared to attend (submitted, approved or no approval needed, not removed) whose visit window overlaps the period.',
  'rpt.def.arrived': 'Participants with at least one observed arrival inside the period.',
  'rpt.def.checked_out': 'Participants whose last observed departure falls inside the period.',
  'rpt.def.denied': 'Participants of visits in the period whose approval was rejected.',
  'rpt.def.cancelled': 'Participants of visits in the period that were cancelled, or who were removed from the visit.',
  'rpt.def.waiting': 'Open waiting episodes at the sites right now.',
  'rpt.def.onsite': 'People whose last observation is an arrival, right now. Recorded presence, not proof of location.',
  'rpt.def.uncertain': 'People whose observations conflict or are under review, right now. They are neither counted as onsite nor as departed.',
  'rpt.def.overdue': 'People observed onsite after their visit window plus the grace period, right now. Their departure is unresolved, never assumed.',
  'rpt.def.people': 'Distinct participants whose visit window overlaps the period. Categories above overlap: one person can be expected, arrived and checked out.',
  'rpt.def.participants': 'Distinct participants (one person on one visit).',
  'rpt.def.outcome.arrived': 'At least one observed arrival.',
  'rpt.def.outcome.cancelled': 'Never arrived; the visit was cancelled or the person removed.',
  'rpt.def.outcome.denied': 'Never arrived; approval rejected.',
  'rpt.def.outcome.no_show': 'Never arrived although cleared; classified a no-show after the cutoff.',
  'rpt.def.outcome.expected': 'None of the above yet.',
  'rpt.def.duration': 'From the first observed arrival to the last observed departure, only when the latest observation is a departure. Median of known durations.',
  'rpt.def.durationUnknown': 'Participants without a recorded departure. Their duration is unknown — never zero and never the time elapsed so far.',
  'rpt.def.noShow': 'Active no-show classifications in the period.',
  'rpt.def.unresolved': 'Active unresolved-departure classifications in the period.',
  'rpt.def.withdrawn': 'Classifications withdrawn because later data disproved them. They are kept, not deleted.',
  'rpt.metric.noShowActive': 'No-shows (active)',
  'rpt.metric.unresolvedActive': 'Unresolved departures (active)',
  'rpt.metric.withdrawn': 'Withdrawn classifications',
  'rpt.metric.attendances': 'Attendances',
  'rpt.metric.compliantAtArrival': 'Compliant at arrival',
  'rpt.metric.nonCompliantAtArrival': 'Not compliant at arrival',
  'rpt.metric.workers': 'Workers',
  'rpt.def.attendances': 'Observed contractor arrivals in the period.',
  'rpt.def.compliantAtArrival': 'Arrivals where the contractor evidence fact was “accepted” at the moment of arrival.',
  'rpt.def.nonCompliantAtArrival': 'Arrivals where the evidence fact was anything else at arrival. A later renewal does not change this.',
  'rpt.def.workers': 'Distinct people among those arrivals.',
  'rpt.metric.vehicleEntries': 'Vehicle entries',
  'rpt.metric.vehicleExits': 'Vehicle exits',
  'rpt.metric.distinctVehicles': 'Distinct vehicles',
  'rpt.metric.anprReads': 'Camera reads',
  'rpt.metric.anprDuplicates': 'Repeated reads (not counted)',
  'rpt.metric.unmatchedReads': 'Unmatched or unclear reads',
  'rpt.metric.gateExceptions': 'Gate exceptions',
  'rpt.metric.parkingAllocations': 'Parking allocations',
  'rpt.metric.vehiclesOnsiteNow': 'Vehicles onsite now',
  'rpt.def.vehicleEntries': 'Recorded vehicle entry movements in the period, excluding voided ones. Camera reads alone are not entries.',
  'rpt.def.vehicleExits': 'Recorded vehicle exit movements in the period, excluding voided ones.',
  'rpt.def.distinctVehicles': 'Distinct vehicles with an entry in the period.',
  'rpt.def.anprReads': 'Plate reads in the period, counting a repeated read of the same plate once.',
  'rpt.def.anprDuplicates': 'Repeated reads of the same plate from the same camera inside the duplicate window. Shown, never added to arrivals.',
  'rpt.def.unmatchedReads': 'Reads that matched no vehicle, several vehicles, or were unreadable or low confidence.',
  'rpt.def.gateExceptions': 'Plate exceptions raised for review in the period.',
  'rpt.def.parkingAllocations': 'Parking allocations whose time overlaps the period.',
  'rpt.def.vehiclesOnsiteNow': 'Vehicles whose last recorded movement is an entry, right now.',

  // ------------------------------------------------------------ reporting: columns, groups, values
  'rpt.col.site': 'Site',
  'rpt.col.timeZone': 'Time zone',
  'rpt.col.visits': 'Visits',
  'rpt.col.participants': 'Participants',
  'rpt.col.durationKnown': 'Known durations',
  'rpt.col.durationUnknown': 'Unknown durations',
  'rpt.col.durationMean': 'Mean duration (min)',
  'rpt.col.durationMedian': 'Median duration (min)',
  'rpt.col.reference': 'Reference',
  'rpt.col.visitor': 'Visitor',
  'rpt.col.organisation': 'Organisation',
  'rpt.col.category': 'Visitor type',
  'rpt.col.host': 'Host',
  'rpt.col.department': 'Department',
  'rpt.col.windowStart': 'Window start',
  'rpt.col.arrival': 'Arrival',
  'rpt.col.departure': 'Departure',
  'rpt.col.duration': 'Duration (min)',
  'rpt.col.outcome': 'Outcome',
  'rpt.col.exception': 'Exception',
  'rpt.col.classification': 'Classification',
  'rpt.col.classifiedAt': 'Classified',
  'rpt.col.withdrawnAt': 'Withdrawn',
  'rpt.col.withdrawnReason': 'Why withdrawn',
  'rpt.col.worker': 'Worker',
  'rpt.col.company': 'Company',
  'rpt.col.companyStatusNow': 'Company status now',
  'rpt.col.evidenceAtArrival': 'Evidence at arrival',
  'rpt.col.evidenceNow': 'Evidence now',
  'rpt.col.approvalAtArrival': 'Approval at arrival',
  'rpt.col.activePermitsNow': 'Approved permits now',
  'rpt.col.evidenceExpiredNow': 'Accepted evidence now expired',
  'rpt.col.evidenceExpiring30': 'Evidence expiring within 30 days',
  'rpt.group.none': 'Total',
  'rpt.group.site': 'Site',
  'rpt.group.day': 'Day',
  'rpt.group.category': 'Visitor type',
  'rpt.group.host': 'Host',
  'rpt.group.department': 'Department',
  'rpt.group.organisation': 'Organisation',
  'rpt.group.destination': 'Destination',
  'rpt.group.purpose': 'Purpose',
  'rpt.group.source': 'Source (planned / walk-in)',
  'rpt.outcome.arrived': 'Arrived',
  'rpt.outcome.cancelled': 'Cancelled',
  'rpt.outcome.denied': 'Denied',
  'rpt.outcome.no_show': 'No-show',
  'rpt.outcome.expected': 'Expected / pending',
  'rpt.exception.no_show': 'No-show',
  'rpt.exception.unresolved_departure': 'Unresolved departure',
  'rpt.classification.active': 'Active',
  'rpt.classification.withdrawn': 'Withdrawn',
  'rpt.withdrawn.arrival_recorded': 'Arrival recorded later',
  'rpt.withdrawn.departure_recorded': 'Departure recorded',
  'rpt.withdrawn.no_longer_applicable': 'No longer applies',
  'rpt.basis.now': 'now',
  'rpt.basis.siteLocal': 'Each site’s local calendar day',
  'rpt.basis.utc': 'UTC calendar day for every site',
  'rpt.unknown': 'Unknown',

  // ------------------------------------------------------------ reporting: notes
  'rpt.note.overlapping': 'These figures overlap: one person can be expected, arrived and checked out in the same period. They are never added up as if they were different people.',
  'rpt.note.nowMetrics': 'Waiting, onsite, uncertain and overdue describe the present moment, whatever period is selected.',
  'rpt.note.presenceNotLocation': 'Presence is what was recorded at desks, kiosks and gates — not proof of where someone physically is.',
  'rpt.note.durationDefinition': 'Duration runs from the first observed arrival to the last observed departure. Without a recorded departure the duration is unknown.',
  'rpt.note.outcomeExclusive': 'Each participant has exactly one outcome, so outcome columns add up to the participants.',
  'rpt.note.attributionHistorical': 'Departments as recorded when the host was assigned to each visit; a later transfer does not move past visits.',
  'rpt.note.attributionCurrent': 'Departments as they are today: a host who moved department takes their past visits with them in this view.',
  'rpt.note.daySiteLocal': 'Days are each site’s own local calendar days.',
  'rpt.note.dayUtc': 'Days are UTC calendar days for every site.',
  'rpt.note.exceptionHistory': 'Classifications are kept when later data withdraws them, with the reason.',
  'rpt.note.noShowCutoff': 'A cleared visitor becomes a no-show after the site’s no-show cutoff (by default, the end of the visit window) without an arrival.',
  'rpt.note.complianceAtArrival': '“At arrival” uses the facts captured when the arrival was observed. A later renewal or acceptance never makes an earlier attendance compliant.',
  'rpt.note.evidenceRollup': 'The evidence fact for contractor visitor types is the roll-up of contractor requirements (company, worker, permit evidence).',
  'rpt.note.vehicleSeparate': 'Vehicle movements are counted separately from people and from camera reads.',
  'rpt.note.anprDuplicates': 'A plate read repeatedly by the same camera counts once; repeats are shown separately.',

  // ------------------------------------------------------------ reporting: exports
  'rpt.sheet.data': 'Report',
  'rpt.sheet.about': 'About this export',
  'rpt.export.generatedAt': 'Generated',
  'rpt.export.scope': 'Scope',
  'rpt.export.definition': 'Report',
  'rpt.export.period': 'Period',
  'rpt.export.timeBasis': 'Day boundaries',
  'rpt.export.grouping': 'Grouping',
  'rpt.export.requestedBy': 'Requested by',
  'rpt.export.digest': 'Filter fingerprint',
  'rpt.export.note': 'Note',
  'rpt.export.personalData': 'CONFIDENTIAL — contains personal data. Handle under your organisation’s data-protection rules and delete when no longer needed.',
  'rpt.export.truncated': 'This export was cut at its row limit. Narrow the filters for a complete export.',
  'rpt.export.openReviews': { one: '{count} presence record is under review; presence figures include uncertain records.', other: '{count} presence records are under review; presence figures include uncertain records.' },
  'rpt.filter.locations': 'Destinations',
  'rpt.filter.categories': 'Visitor types',
  'rpt.filter.organisations': 'Organisations',
  'rpt.filter.hosts': 'Hosts',
  'rpt.filter.states': 'Outcomes / states',

  // ------------------------------------------------------------ audit export and detail
  'audit.export.title': 'Passage audit export',
  'audit.export.confidential': 'CONFIDENTIAL — audit records. Export recorded in the audit trail.',
  'audit.export.changesWithheld': 'Before/after values were requested but withheld: they need the sensitive-audit capability on the whole scope.',
  'audit.export.window': 'Time window',
  'audit.export.allScope': 'All sites in scope and organisation-level events',
  'audit.filter.action': 'Action',
  'audit.filter.actor': 'Actor',
  'audit.filter.outcome': 'Outcome',
  'audit.filter.resourceType': 'Record type',
  'audit.filter.resourceId': 'Record',
  'audit.filter.correlation': 'Correlation reference',
  'audit.col.actor': 'Actor',
  'audit.col.resource': 'Record',
  'audit.col.correlation': 'Correlation reference',
  'audit.col.changes': 'Before / after',

  // ------------------------------------------------------------ API messages and validation
  'rpt.api.entitlement': 'This report needs an add-on your organisation does not have.',
  'rpt.api.multiSite': 'Reports across several sites need the multi-site capability. Choose one site.',
  'rpt.api.staleFilter': 'The filters changed since these results were shown. Run the report again, then export.',
  'rpt.api.exportExpired': 'This export has expired. Request a new one.',
  'rpt.api.exportNotReady': 'This export is not ready to download.',
  'rpt.v.sites': 'Choose at least one site.',
  'rpt.v.rangeOrder': 'The end must not be before the start.',
  'rpt.v.rangeTooLong': 'Choose a period of at most 366 days.',
  'rpt.v.grouping': 'This report cannot be grouped that way.',
  'rpt.v.recipients': 'Choose at least one recipient.',
  'rpt.v.recipientUnknown': 'Each recipient must be an active user of this organisation.',
  'audit.v.rangeRequired': 'Give both a start and an end to search the audit trail.',
  'audit.v.rangeBroad': 'A search without an actor, record or reference can cover at most 31 days.',
  'audit.v.rangeNarrow': 'A search can cover at most 366 days.',
  'saf.api.musterOpen': 'A muster is already open for this site. Continue it instead of starting another.',
  'saf.api.musterClosed': 'This muster is closed. Its record is final.',
  'saf.api.nothingToReconcile': 'There is no conflict to reconcile for this person.',
  'saf.api.reviewResolved': 'This presence review is already resolved.',
  'saf.api.sourceExists': 'A presence source with this key already exists at this site.',
  'saf.api.sourceDisabled': 'This presence source is disabled.',
  'saf.api.exportExpired': 'This roster export has expired. Create a new one.',
  'saf.api.exportIntegrity': 'The roster could not be reproduced exactly as generated. Create a new export.',
  'saf.v.sourceKey': 'Use 1–64 lowercase letters, numbers, dots, hyphens or underscores.',
  'saf.v.confirm': 'Confirm that you have read the freshness warning.',
  'saf.v.basis': 'That observation does not belong to this person.',
  'saf.v.beforeSnapshot': 'An observation cannot be earlier than the roster snapshot.',
  'saf.v.reconcileNote': 'Explain how you reconciled the conflicting observations.',
  'saf.v.watermarkFuture': 'A source cannot confirm observations in the future.',

  // ------------------------------------------------------------ roster export and values
  'saf.export.title': 'Passage muster roster',
  'saf.export.drill': 'DRILL — this is a practice muster.',
  'saf.export.emergency': 'EMERGENCY MUSTER',
  'saf.export.notLocation': 'This roster lists recorded observations. It is not proof of anyone’s physical location: check every person.',
  'saf.export.generated': 'Generated {time}. Anything recorded after this time is not on this page.',
  'saf.export.stale': 'STALE-DATA RISK: at the snapshot some presence sources had not reported recently ({sources}). People may be missing or wrongly listed.',
  'saf.export.freshnessAtSnapshot': 'Presence sources at the snapshot: confirmed through {time}.',
  'saf.export.unresolved': { one: '{count} person was overdue (departure unresolved) at the snapshot.', other: '{count} people were overdue (departure unresolved) at the snapshot.' },
  'saf.export.reviews': { one: '{count} presence record was under review at the snapshot.', other: '{count} presence records were under review at the snapshot.' },
  'saf.export.site': 'Site',
  'saf.export.snapshotAt': 'Roster snapshot',
  'saf.export.asOf': 'Roll call as of',
  'saf.export.assembly': 'Assembly point',
  'saf.export.totals': 'Whole event',
  'saf.export.filtered': 'This page (filtered)',
  'saf.totals.line': '{roster} on roster · {accounted} accounted for · {unaccounted} not accounted for',
  'saf.freshness.synchronousOnly': 'every source records directly in Passage',
  'saf.col.name': 'Name',
  'saf.col.kind': 'Type',
  'saf.col.organisation': 'Organisation',
  'saf.col.host': 'Responsible host',
  'saf.col.destination': 'Destination / zone',
  'saf.col.atSnapshot': 'At snapshot',
  'saf.col.lastObserved': 'Last observation',
  'saf.col.rollCall': 'Roll call',
  'saf.col.observer': 'Observer',
  'saf.col.observedAt': 'Observed',
  'saf.col.lateFacts': 'Later facts',
  'saf.flag.overdue': 'overdue',
  'saf.kind.visitor': 'Visitor',
  'saf.kind.contractor': 'Contractor',
  'saf.kind.courier': 'Courier',
  'saf.occupancy.onsite': 'Observed onsite',
  'saf.occupancy.uncertain': 'Presence uncertain',
  'saf.rollcall.safe': 'Safe',
  'saf.rollcall.missing': 'Missing',
  'saf.rollcall.departed': 'Departed',
  'saf.rollcall.unknown': 'Unknown',
  'saf.rollcall.needs_assistance': 'Needs assistance',
  'saf.rollcall.not_checked': 'Not yet checked',
  'saf.rollcall.conflict': 'Conflicting reports',
  'saf.late.departed_before_snapshot': 'Departed before the snapshot (recorded later)',
  'saf.late.departed_after_snapshot': 'Departure recorded after the snapshot',
  'saf.late.arrived_after_snapshot': 'Arrived after the snapshot',
  'saf.late.returned_after_snapshot': 'Arrival recorded after the snapshot',

  // ------------------------------------------------------------ email
  'saf.mail.drill': 'DRILL',
  'saf.mail.emergency': 'EMERGENCY',
  'saf.mail.subject': 'Muster started at {site}',
  'saf.mail.body': 'Dear {name},\n\nA muster has started at {site}. Assembly point: {assembly}.\n\nYou are the responsible host for {count} person(s) on the roster: {people}.\n\nIf you know where they are, tell the emergency coordinator. Follow your site’s emergency procedure; this message is not an alarm.',
  'rpt.mail.subject': 'Your scheduled report “{report}” is ready',
  'rpt.mail.body': 'Dear {name},\n\nYour scheduled report “{report}” is ready in Passage until {expires}. Sign in to download it; your access is checked again when you do.\n\nThe report itself is not attached to this email.',

  // ------------------------------------------------------------ safety UI (module 14)
  'saf.ui.title': 'Occupancy & muster',
  'saf.ui.intro': 'Account for people. Expose uncertainty. Presence here is what desks, kiosks and gates recorded — not proof of where anyone is.',
  'saf.ui.tab.occupancy': 'Live occupancy',
  'saf.ui.tab.muster': 'Muster',
  'saf.ui.tab.reviews': 'Presence reviews',
  'saf.ui.tab.sources': 'Presence sources',
  'saf.ui.tab.settings': 'Safety settings',
  'saf.ui.m.onsite': 'Observed onsite',
  'saf.ui.m.onsiteSub': 'Last observation is an arrival',
  'saf.ui.m.uncertain': 'Presence uncertain',
  'saf.ui.m.uncertainSub': 'Conflicting or under review',
  'saf.ui.m.overdue': 'Overdue',
  'saf.ui.m.overdueSub': 'Departure unresolved, never assumed',
  'saf.ui.m.vehicles': 'Vehicles onsite',
  'saf.ui.m.vehiclesSub': 'Counted separately from people',
  'saf.ui.freshness.title': 'Presence sources are current',
  'saf.ui.freshness.staleTitle': 'Stale-data risk',
  'saf.ui.freshness.synchronous': 'Every source records directly in Passage; there is no delayed feed.',
  'saf.ui.freshness.watermark': 'Feeds confirmed complete through',
  'saf.ui.freshness.neverReported': { one: '{count} feed has never reported.', other: '{count} feeds have never reported.' },
  'saf.ui.freshness.staleSources': 'Not reporting recently:',
  'saf.ui.freshness.lastRecorded': 'Latest observation recorded:',
  'saf.ui.freshnessAtSnapshot': 'Presence sources at the snapshot',
  'saf.ui.notLocation': 'Recorded presence is not proof of physical location.',
  'saf.ui.people': 'People onsite or uncertain',
  'saf.ui.peopleSub': { one: '{count} person · couriers: {couriers} · open reviews: {reviews}', other: '{count} people · couriers: {couriers} · open reviews: {reviews}' },
  'saf.ui.search': 'Find a person',
  'saf.ui.filteredNote': 'Showing {shown} of {total}. The counts above are for the whole site.',
  'saf.ui.nobody': 'Nobody is recorded as onsite.',
  'saf.ui.col.presence': 'Presence',
  'saf.ui.col.otherFacts': 'Other facts (independent)',
  'saf.ui.col.action': 'Action',
  'saf.ui.col.anomaly': 'What was detected',
  'saf.ui.col.opened': 'Opened',
  'saf.ui.col.outcome': 'Outcome',
  'saf.ui.col.record': 'Record roll call',
  'saf.ui.col.source': 'Source',
  'saf.ui.col.mode': 'How it reports',
  'saf.ui.col.freshness': 'Freshness',
  'saf.ui.col.confirmedThrough': 'Complete through',
  'saf.ui.overdue': 'Overdue',
  'saf.ui.underReview': { one: '{count} review open', other: '{count} reviews open' },
  'saf.ui.credentialNote': 'Badge, credential and visit state are shown beside presence. None of them adds anyone to this list or removes anyone from it.',
  'saf.ui.couriers': 'Couriers onsite',
  'saf.ui.vehicles': 'Vehicles still onsite',
  'saf.ui.vehiclesSub': 'Vehicle presence is its own fact; a parked vehicle does not mean its driver is onsite.',
  'saf.ui.noVisit': 'No linked visit',
  'saf.ui.openMuster': 'Open the current muster',
  'saf.ui.startMuster': 'Start muster',
  'saf.ui.startSub': 'Capture everyone currently recorded onsite into a fixed roster. This does not trigger an alarm.',
  'saf.ui.startCounts': { one: 'The roster will list 1 person; {overdue} overdue and {reviews} presence records under review.', other: 'The roster will list {count} people; {overdue} overdue and {reviews} presence records under review.' },
  'saf.ui.kind': 'Type of event',
  'saf.ui.kind.drill': 'Drill',
  'saf.ui.kind.emergency': 'Real emergency',
  'saf.ui.note': 'Note (optional)',
  'saf.ui.confirmFreshness': 'I have read the freshness warning and understand the roster may be incomplete.',
  'saf.ui.startDrill': 'Start drill',
  'saf.ui.startEmergency': 'Start emergency muster',
  'saf.ui.flagReview': 'Flag presence for review',
  'saf.ui.flagReviewSub': 'Use this when you have reason to doubt the recorded presence. The person is shown as uncertain until someone resolves it.',
  'saf.ui.reason': 'Reason',
  'saf.ui.presenceHistory': 'Presence history',
  'saf.ui.presenceHistorySub': 'Every observation and every reviewed correction, in order. Nothing here is edited.',
  'saf.ui.observations': 'Observations',
  'saf.ui.recordedLate': 'recorded later',
  'saf.ui.reviews': 'Presence reviews',
  'saf.ui.reviewsSub': 'Late, duplicate or contradictory observations from any source open a review instead of a confident answer.',
  'saf.ui.status': 'Status',
  'saf.ui.reviewStatus.open': 'Open',
  'saf.ui.reviewStatus.resolved': 'Resolved',
  'saf.ui.reviewStatus.all': 'All',
  'saf.ui.noReviews': 'No presence reviews here.',
  'saf.ui.detectedBySystem': 'Detected automatically',
  'saf.ui.resolve': 'Resolve review',
  'saf.ui.resolveSub': 'Your decision is recorded beside the observations; the observations themselves never change.',
  'saf.ui.event.arrival': 'Arrival',
  'saf.ui.event.departure': 'Departure',
  'saf.ui.source.reception': 'Reception desk',
  'saf.ui.source.kiosk': 'Kiosk',
  'saf.ui.source.gate': 'Gate',
  'saf.ui.source.system': 'System',
  'saf.ui.source.access_control': 'Access control',
  'saf.ui.source.edge_agent': 'Edge agent',
  'saf.ui.source.other': 'Other',
  'saf.anomaly.out_of_order': 'Observation arrived late, out of order',
  'saf.anomaly.duplicate_arrival': 'Arrival while already onsite',
  'saf.anomaly.departure_without_arrival': 'Departure without an arrival',
  'saf.anomaly.manual': 'Flagged by staff',
  'saf.resolution.confirmed_onsite': 'Confirmed onsite',
  'saf.resolution.confirmed_departed': 'Confirmed departed',
  'saf.resolution.remains_unknown': 'Still unknown',
  'saf.resolution.no_change': 'Recorded presence is right',
  'saf.ui.noOpenMuster': 'No muster is open for this site.',
  'saf.ui.musterHistory': 'Musters at this site',
  'saf.ui.noMusters': 'No muster has been started here yet.',
  'saf.ui.musterStatus.open': 'Open',
  'saf.ui.musterStatus.closed': 'Closed',
  'saf.ui.snapshotLine': 'Assembly point: {assembly}. Started by {by}. Snapshot:',
  'saf.ui.totals.roster': 'On the roster',
  'saf.ui.totals.accounted': 'Accounted for',
  'saf.ui.totals.unaccounted': 'Not accounted for',
  'saf.ui.totals.wholeEvent': 'Whole event',
  'saf.ui.totals.unaccountedSub': 'Whole event · conflicts: {conflict} · missing: {missing}',
  'saf.ui.totals.filtered': 'Filtered: not accounted / shown',
  'saf.ui.totals.filteredSub': 'Only the filtered list — never the event total',
  'saf.ui.roster': 'Roll call',
  'saf.ui.rosterSub': 'The snapshot is fixed. Each observation is added, never overwritten; disagreements wait for reconciliation.',
  'saf.ui.any': 'Any',
  'saf.ui.method': 'How it was confirmed',
  'saf.method.in_person': 'In person',
  'saf.method.radio': 'Radio',
  'saf.method.phone': 'Phone',
  'saf.method.list_check': 'List check',
  'saf.method.other': 'Other',
  'saf.ui.reconcile': 'Reconcile',
  'saf.ui.reconcileSub': { one: '{count} observation disagrees. Choose the status you confirmed and say how.', other: '{count} observations disagree. Choose the status you confirmed and say how.' },
  'saf.ui.reconciled': 'reconciliation',
  'saf.ui.history': 'History',
  'saf.ui.historySub': 'Snapshot, every roll-call observation, later presence facts and exports.',
  'saf.ui.exports': 'Exports',
  'saf.ui.exportPrint': 'Printable roster',
  'saf.ui.exportCsv': 'CSV roster',
  'saf.ui.download': { one: 'Download ({rows} row)', other: 'Download ({rows} rows)' },
  'saf.ui.rows': { one: '{count} row', other: '{count} rows' },
  'saf.ui.close': 'Close muster',
  'saf.ui.closeSub': 'A reason is required, even if people remain unaccounted for. A closed muster cannot be changed.',
  'saf.ui.alsoKnown': 'Also known',
  'saf.ui.notices': 'Host notices — sent: {accepted}, waiting: {queued}, not delivered: {failed}.',
  'saf.ui.empty': 'Nobody was recorded onsite at the snapshot.',
  'saf.ui.sources': 'Presence sources',
  'saf.ui.sourcesSub': 'Desks and kiosks write directly. Feeds report how far they are complete; a feed that stops reporting makes the roster stale.',
  'saf.ui.mode.synchronous': 'Directly',
  'saf.ui.mode.feed': 'Delayed feed',
  'saf.ui.fresh.current': 'Current',
  'saf.ui.fresh.stale': 'Stale',
  'saf.ui.fresh.never_reported': 'Never reported',
  'saf.ui.fresh.disabled': 'Disabled',
  'saf.ui.disable': 'Disable',
  'saf.ui.enable': 'Enable',
  'saf.ui.sourceKey': 'Source key',
  'saf.ui.sourceLabel': 'Name',
  'saf.ui.addSource': 'Register source',
  'saf.ui.settings': 'Safety settings',
  'saf.ui.settingsDefault': 'This site uses the default values. Saving creates the site’s own settings.',
  'saf.ui.settingsSub': 'Changes are recorded in the audit trail.',
  'saf.ui.set.staleAfterMinutes': 'A feed is stale after (minutes)',
  'saf.ui.set.exportTtlMinutes': 'Roster exports expire after (minutes)',
  'saf.ui.set.notifyHostsOnEmergency': 'Email responsible hosts when an emergency muster starts',
  'saf.ui.set.notifyHostsOnDrill': 'Email responsible hosts when a drill starts',
  'saf.ui.set.includeCouriers': 'Include couriers onsite in the roster',
  'saf.ui.set.noShowAfterMinutes': 'No-show after (minutes from window start)',
  'saf.ui.set.noShowHint': 'Leave empty to classify a no-show only when the visit window has ended.',

  // ------------------------------------------------------------ reports UI (module 15)
  'rpt.ui.eyebrow': 'Passage / Reports',
  'rpt.ui.title': 'Aggregate reports',
  'rpt.ui.intro': 'Understand the operation, not the identity. Every figure says what it counts, what it leaves out and how fresh it is.',
  'rpt.ui.filters': 'Report filters',
  'rpt.ui.report': 'Report',
  'rpt.ui.unavailable': 'not available for your organisation',
  'rpt.ui.sites': 'Sites',
  'rpt.ui.singleSite': 'Combining sites needs the multi-site capability.',
  'rpt.ui.from': 'From',
  'rpt.ui.to': 'To',
  'rpt.ui.attribution': 'Department attribution',
  'rpt.ui.attribution.historical': 'As recorded at each visit',
  'rpt.ui.attribution.current': 'Current department',
  'rpt.ui.listHint': 'Separate several values with commas.',
  'rpt.ui.state.arrived': 'Arrived',
  'rpt.ui.state.cancelled': 'Cancelled',
  'rpt.ui.state.denied': 'Denied',
  'rpt.ui.state.no_show': 'No-show',
  'rpt.ui.state.expected': 'Expected / pending',
  'rpt.ui.state.unresolved_departure': 'Unresolved departure',
  'rpt.ui.state.active': 'Active classification',
  'rpt.ui.state.withdrawn': 'Withdrawn classification',
  'rpt.ui.detail': 'Show identifiable rows (recorded in the audit trail)',
  'rpt.ui.run': 'Run report',
  'rpt.ui.stale': 'The filters changed since these results were produced. Run the report again before exporting.',
  'rpt.ui.scope': 'Scope: {sites}',
  'rpt.ui.asOf': 'As of',
  'rpt.ui.identifiable': 'Identifiable rows',
  'rpt.ui.aggregate': 'Aggregate only',
  'rpt.ui.classifiedAt': 'Exceptions last classified {time} UTC.',
  'rpt.ui.neverClassified': 'Exceptions have not been classified yet for these sites.',
  'rpt.ui.chart': 'Chart of {metric}',
  'rpt.ui.page': 'Rows {from}–{to}',
  'rpt.ui.pageReset': 'filters changed, back to the first page',
  'rpt.ui.nextPage': 'Next page',
  'rpt.ui.format': 'Format',
  'rpt.ui.format.csv': 'CSV',
  'rpt.ui.format.xlsx': 'Excel (XLSX)',
  'rpt.ui.format.html': 'Printable (save as PDF)',
  'rpt.ui.export': 'Request export',
  'rpt.ui.exportQueued': 'Export requested. It appears below when ready.',
  'rpt.ui.exports': 'Your exports',
  'rpt.ui.exportsSub': 'Exports expire. Your access is checked again when you download.',
  'rpt.ui.job.queued': 'Being prepared',
  'rpt.ui.job.ready': 'Ready',
  'rpt.ui.job.failed': 'Failed',
  'rpt.ui.job.denied': 'Refused — access changed',
  'rpt.ui.job.expired': 'Expired',
  'rpt.ui.download': 'Download',
  'rpt.ui.schedules': 'Scheduled reports',
  'rpt.ui.schedulesSub': 'Recipients are checked again at every run; anyone who lost access is withheld, never emailed.',
  'rpt.ui.newSchedule': 'Schedule this report',
  'rpt.ui.newScheduleSub': 'Recipients receive a notice that the report is ready in Passage — the report itself is never emailed.',
  'rpt.ui.noSchedules': 'No scheduled reports yet.',
  'rpt.ui.scheduleName': 'Name',
  'rpt.ui.cadence': 'How often',
  'rpt.ui.cadence.daily': 'Daily',
  'rpt.ui.cadence.weekly': 'Weekly',
  'rpt.ui.cadence.monthly': 'Monthly',
  'rpt.ui.weekday': 'Day of week (1 = Monday)',
  'rpt.ui.monthDay': 'Day of month (1–28)',
  'rpt.ui.localTime': 'Local time',
  'rpt.ui.period.previous_day': 'The previous day',
  'rpt.ui.period.previous_7_days': 'The previous 7 days',
  'rpt.ui.period.previous_month': 'The previous calendar month',
  'rpt.ui.recipients': 'Recipients',
  'rpt.ui.recipientsHint': 'Only people who can read these reports on every chosen site are listed.',
  'rpt.ui.scheduleStatus.active': 'Active',
  'rpt.ui.scheduleStatus.disabled': 'Disabled',
  'rpt.ui.nextRun': 'next run',
  'rpt.ui.wouldBeWithheld': 'would be withheld',

  // ------------------------------------------------------------ audit UI
  'audit.ui.search': 'Search',
  'audit.ui.clear': 'Clear',
  'audit.ui.actionHint': 'An exact action, or a prefix ending in a dot such as “auth.”',
  'audit.ui.breadth': 'Searches without an actor, record or reference can cover up to 31 days; narrower searches up to 366 days.',
  'audit.ui.detail': 'Audit event',
  'audit.ui.detailSub': 'Read-only. Opening before/after values is itself recorded.',
  'audit.ui.changesHidden': 'Before/after values need the separate sensitive-audit capability.',
  'audit.ui.correlation': 'Correlated activity',
  'audit.ui.correlationSub': 'Everything recorded under one reference in this organisation.',
  'audit.ui.events': 'Audit events',
  'audit.ui.jobs': 'Background jobs',
  'audit.ui.providers': 'Provider references',
  'audit.ui.attempts': { one: '{count} attempt', other: '{count} attempts' },
  'audit.ui.jobsOrgOnly': 'Background jobs and provider references are shown to organisation-wide auditors only.',
  'audit.ui.export': 'Export audit records',
  'audit.ui.exportSub': 'Needs the audit export capability. The export and every download are recorded.',
  'audit.ui.includeChanges': 'Include before/after values (needs the sensitive-audit capability)',
  'audit.ui.exportQueued': 'Export requested. It appears in your exports when ready.',
} as const;
